import { Injectable } from '@angular/core';
import { ApolloQueryResult, FetchPolicy } from 'apollo-client';
import cleanDeep from 'clean-deep';
import gql from 'graphql-tag';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { SymptomModes } from 'src/app/shared/symptoms/symptom.model';
import { environment } from 'src/environments/environment';
import {
  AssessmentType,
  CreateAssessmentInput,
  CreateAssessmentMutation,
  CreatePatientPortalGrantInput,
  GetAssessmentQuery,
  GetAssessmentRequestWithDoctorQuery,
  GetPatientPortalGrantQuery,
  ListAssessmentsQuery,
  ModelSortDirection,
  UpdateAssessmentInput,
  UpdateAssessmentMutation,
  UpdateAssessmentRequestInput
} from '../../../API';
import {
  getAllAssessmentInfo,
  getAssessmentRequestWithDoctor,
  lockAssessment
} from '../../../graphql/custom_queries';
import {
  createAssessment,
  createAssessmentRequest,
  createPatientPortalGrant,
  updateAssessment,
  updateAssessmentRequest
} from '../../../graphql/mutations';
import { Attachment } from '../../shared/attachments/attachments.component';
import { AppSyncService } from '../appsync.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { StaffPermissionsService } from '../services/staff-permissions.service';
import {
  CreateAssessmentRequestInput,
  ListAssessmentsByClinicIdCreatedAtQuery,
  S3ObjectInput
} from './../../../API';
import { S3AttachmentService, S3ObjectConfig } from './../../shared/attachments/s3-attachment.service';
import { PatientService } from './patient.service';

export type Assessment = GetAssessmentQuery['getAssessment'];
export type PatientPortal = GetPatientPortalGrantQuery['getPatientPortalGrant'];
export type AssessmentRequest = GetAssessmentRequestWithDoctorQuery['getAssessmentRequest'];
export type AssessmentList = ListAssessmentsQuery['listAssessments'];

export interface CameraAssessmentRequest {
  assessmentId?: string;
  patientName?: string;
  patientId?: string;
  uploadDate?: number;
  requestDate?: number;
}

export interface PatientPortalGrantAccessSetup {
  portalGrantAccess: CreatePatientPortalGrantInput,
  attachment: Attachment,
  s3ObjectConfig: S3ObjectConfig
}

export interface SendPatientPortalPortalGrantInput extends CreatePatientPortalGrantInput {
  filename: string;
  attachmentFile: File;
  assessmentId: string
}

@Injectable({
  providedIn: 'root'
})
export class AssessmentService {
  public currentAssessment: Assessment;
  private listAssessmentsByClinicIdCreatedAt = /* GraphQL */ `
    query ListAssessmentsByClinicIdCreatedAt(
      $assessmentClinicId: ID
      $createdAt: ModelStringKeyConditionInput
      $sortDirection: ModelSortDirection
      $limit: Int
      $nextToken: String
    ) {
      listAssessmentsByClinicIdCreatedAt(
        assessmentClinicId: $assessmentClinicId
        createdAt: $createdAt
        sortDirection: $sortDirection
        limit: $limit
        nextToken: $nextToken
      ) {
        items {
          id
          patient {
            id
            firstName
            lastName
            email
            phone
            agreedToTerms
            dateOfBirth
            gender
            genderOther
            address
            createdAt
            updatedAt
            nonModifiableData
          }
          assessmentClinicId
          type
          body
          attachments {
            bucket
            key
            region
            fileName
            metadata
          }
          locked
          createdAt
          updatedAt
        }
        nextToken
      }
    }
  `;

  public get isViewOnly(): boolean {
    return !this.staffPermissionsService.permissions.canEditAssessment;
  }

  public readonly encounterMethodFriendlyName = {
    advanced: 'Gold Standard',
    silver: 'Accelerated Dry Eye Assessment',
    preOpScreening: 'Pre-Operative Screening',
    simple: 'Screening',
    preOp: 'ASCRS Pre-Op',
    preop: 'ASCRS Pre-Op',
    treatment: 'Treatment',
    followUp: 'Follow Up',
    dewsIII: 'DEWS III'
  };

  public encounterMethodToDescriptionMap: { [key in SymptomModes]?: any } = {
    [SymptomModes.PreOpScreening]: 'Five step assessment of ocular surface (SPEED is recommended)',
    [SymptomModes.Advanced]: 'Core dry eye assessment (Intake form / OSDI and SPEED recommended)',
    [SymptomModes.FollowUp]: 'A focused version for subsequent check-ups'
  };

  constructor(
    private appSyncService: AppSyncService,
    private authService: AuthenticationService,
    private patientService: PatientService,
    private s3AttachmentService: S3AttachmentService,
    private clinicSetupService: ClinicSetupService,
    private staffPermissionsService: StaffPermissionsService
  ) {}


  public createPatientPortalGrantAccess(
    config: SendPatientPortalPortalGrantInput
  ) {
    const { attachmentFile, assessmentId, filename, ...input } = config;
    const s3ObjectInput = this.getS3ObjectConfig({ ...config });
    return this.prepAttachmentForUpload(attachmentFile, s3ObjectInput).pipe(
      switchMap(s3ObjectInput => {
        if (s3ObjectInput) {
          input.s3Object = s3ObjectInput
        }
        return this.appSyncService.hydrated();
      }),
      switchMap(client => {
        return client.mutate({
          mutation: gql(createPatientPortalGrant),
          variables: { input: input }
        })
      }),
      map(result => result.data.createPatientPortalGrant)
    )

  }

  public prepAttachmentForUpload(attachment: Attachment, s3ObjectConfig: S3ObjectConfig) {
    return of(attachment).pipe(
      map(attachment => {
        return this.s3AttachmentService.uploadToSpecificS3Bucket(s3ObjectConfig, attachment)
      }))
  }


  public createAssessment(
    type: AssessmentType,
    body: any,
    attachments: Attachment[],
    patientId?: string,
    doctorId?: string,
    isLocked = false,
    createdAt: any = null
  ): Observable<Assessment> {
    const input: CreateAssessmentInput = {
      type: type,
      // Dynamo does not accept empty fields so remove them via cleanDeep
      // see https://forums.aws.amazon.com/thread.jspa?messageID=870962&#870962
      body: JSON.stringify(cleanDeep(body)),
      assessmentPatientId: patientId,
      assessmentDoctorId: doctorId,
      locked: isLocked,
      createdAt
    };

    return this.prepAttachmentsForUpload(attachments).pipe(
      switchMap(s3ObjectInputs => {
        if (s3ObjectInputs) {
          input.attachments = s3ObjectInputs;
        }
        return this.appSyncService.hydrated();
      }),
      switchMap(client => {
        return client.mutate({
          mutation: gql(createAssessment),
          variables: { input: input },
          update: (proxy, mutationResult) => {
            if (!!patientId) {
              this.patientService.updateAssessmentsForPatient(
                proxy,
                mutationResult.data as CreateAssessmentMutation,
                patientId
              );
            }
          }
        });
      }),
      map(result => result.data.createAssessment)
    );
  }

  public updateAssessment(
    id: string,
    type: AssessmentType,
    body: any,
    attachments: Attachment[],
    patientId: string,
    doctorId?: string,
    isLocked?: boolean,
    createdAt: any = null
  ): Observable<Assessment> {
    const input: UpdateAssessmentInput = {
      id: id,
      type: type,
      body: JSON.stringify(cleanDeep(body)),
      assessmentPatientId: patientId,
      assessmentDoctorId: doctorId,
      locked: isLocked,
      createdAt
    };

    if (!createdAt) {
      delete input.createdAt;
    }

    return this.prepAttachmentsForUpload(attachments).pipe(
      switchMap(s3ObjectInputs => {
        input.attachments = s3ObjectInputs;
        return this.appSyncService.hydrated();
      }),
      switchMap(client => {
        return client.mutate({
          mutation: gql(updateAssessment),
          variables: { input: input }
        });
      }),
      map((result: ApolloQueryResult<UpdateAssessmentMutation>) => result.data.updateAssessment)
    );
  }

  public getAssessment(
    id: string,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<Assessment> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query<GetAssessmentQuery>({
          query: gql(getAllAssessmentInfo),
          variables: { id: id },
          fetchPolicy
        })
      ),
      map((result: ApolloQueryResult<GetAssessmentQuery>) => result.data.getAssessment)
    );
  }

  public getAttachments(id: string): Observable<Attachment[]> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query<GetAssessmentQuery>({
          query: gql(/* GraphQL */ `
            query GetAssessment($id: ID!) {
              getAssessment(id: $id) {
                id
                attachments {
                  bucket
                  key
                  region
                  fileName
                  metadata
                }
              }
            }
          `),
          variables: { id: id },
          fetchPolicy: 'network-only'
        })
      ),
      map((result: ApolloQueryResult<GetAssessmentQuery>) => result.data.getAssessment.attachments)
    );
  }

  public unlockAssessment(id: string): Observable<Partial<Assessment>> {
    const input: UpdateAssessmentInput = {
      id: id,
      locked: false
    };

    return this.appSyncService.hydrated().pipe(
      switchMap(client => {
        return client.mutate({
          mutation: gql(lockAssessment),
          variables: { input: input }
        });
      }),
      map((result: ApolloQueryResult<UpdateAssessmentMutation>) => result.data.updateAssessment)
    );
  }

  public prepAttachmentsForUpload(attachments: Attachment[]): Observable<S3ObjectInput[]> {
    return of(attachments).pipe(
      switchMap(attachmentsToUpload => {
        if (attachmentsToUpload && attachmentsToUpload.length > 0) {
          return this.authService.getIdentityId();
        } else {
          return of(null);
        }
      }),
      map(identityId =>
        identityId
          ? attachments.map(attachment =>
            this.s3AttachmentService.convertToS3ObjectInput(identityId, attachment)
          )
          : null
      )
    );
  }

  public createAssessmentRequest(
    input: CreateAssessmentRequestInput
  ): Observable<AssessmentRequest> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client => {
        return client.mutate({
          mutation: gql(createAssessmentRequest),
          variables: { input: input }
        });
      }),
      map(result => result.data.createAssessmentRequest)
    );
  }

  public getAssessmentRequest(id: string): Observable<AssessmentRequest> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client => {
        return client.query({
          query: gql(getAssessmentRequestWithDoctor),
          variables: { id: id }
        });
      }),
      map(result => result.data['getAssessmentRequest'])
    );
  }

  public respondToAssessmentRequest(
    response: string,
    assessmentRequestId: string
  ): Observable<any> {
    const input: UpdateAssessmentRequestInput = {
      id: assessmentRequestId,
      response: response
    };

    return this.appSyncService.hydrated().pipe(
      switchMap(client => {
        return client.mutate({
          mutation: gql(updateAssessmentRequest),
          variables: { input: input }
        });
      }),
      map(result => result.data.updateAssessmentRequest)
    );
  }

  public isAssessment(value: any) {
    return (<Assessment>value).__typename && (<Assessment>value).__typename === 'Assessment';
  }

  public listAssessmentsByDateRange(
    dateRange: [string, string],
    options: {
      limit?: number;
      sortDirection?: ModelSortDirection;
      fetchPolicy?: FetchPolicy;
    } = {}
  ): Observable<Assessment[]> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query({
          query: gql(this.listAssessmentsByClinicIdCreatedAt),
          variables: {
            assessmentClinicId: this.clinicSetupService.clinicId,
            createdAt: { between: dateRange },
            sortDirection: options.sortDirection,
            limit: options.limit
          },
          fetchPolicy: options.fetchPolicy
        })
      ),
      map(
        (assessmentQuery: ApolloQueryResult<ListAssessmentsByClinicIdCreatedAtQuery>) =>
          assessmentQuery.data.listAssessmentsByClinicIdCreatedAt.items as Assessment[]
      )
    );
  }

  groupAssessmentsByPatient(assessments: Assessment[]) {
    const patientAssessmentGroup = {};

    assessments.forEach(assessment => {
      patientAssessmentGroup[assessment.patient.id] = {
        patient: assessment.patient,
        assessments: [
          ...((patientAssessmentGroup[assessment.patient.id] &&
            patientAssessmentGroup[assessment.patient.id].assessments) ||
            []),
          assessment
        ]
      };
    });

    return patientAssessmentGroup;
  }

  private getS3ObjectConfig({ patientPortalGrantClinicId, patientId, assessmentId, filename }): S3ObjectConfig {
    const bucketConfig = environment.production ? environment.fileUpload.caCentral : environment.fileUpload.usEast;
    const s3Config: S3ObjectConfig = {
      bucketName: bucketConfig.bucket,
      key: `${patientPortalGrantClinicId}/${patientId}/${assessmentId}/${filename}`,
      region: bucketConfig.region,
      metadata: JSON.stringify({
        patientId: patientId,
        assessmentId: assessmentId,
        clinicId: patientPortalGrantClinicId,

      })
    }
    return s3Config;

  }

}

